import { Dispatch, SetStateAction, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Modal, Box, IconButton } from "@mui/material";

interface ImageCarouselModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  images: string[];
  title?: string;
}

export default function ImageCarouselModal({
  open,
  onClose,
  setOpen,
  images,
  title = "Images",
}: ImageCarouselModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset to first image when modal opens or images change
  useEffect(() => {
    if (open && images.length > 0) {
      setCurrentImageIndex(0);
    }
  }, [open, images]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  // Handle next image in carousel
  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev < images.length - 1 ? prev + 1 : 0
    );
  };

  // Handle previous image in carousel
  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : images.length - 1
    );
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "90%",
          maxWidth: "800px",
          maxHeight: "90vh",
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 24,
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ fontSize: "18px", fontWeight: 600 }}>
            {title} ({currentImageIndex + 1} / {images.length})
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Image Carousel */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            minHeight: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
            overflow: "hidden",
          }}
        >
          {/* Previous Button */}
          {images.length > 1 && (
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: "absolute",
                left: 10,
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* Current Image */}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={images[currentImageIndex] || "/static/images/placeholder.png"}
              alt={`Image ${currentImageIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "500px",
                objectFit: "contain",
                borderRadius: "8px",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "/static/images/placeholder.png";
              }}
            />
          </Box>

          {/* Next Button */}
          {images.length > 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: "absolute",
                right: 10,
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>

        {/* Image Thumbnails */}
        {images.length > 1 && (
          <Box
            sx={{
              padding: 2,
              borderTop: "1px solid #e0e0e0",
              overflowX: "auto",
              display: "flex",
              gap: 1,
              justifyContent: "center",
            }}
          >
            {images.map((img, index) => (
              <Box
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  overflow: "hidden",
                  cursor: "pointer",
                  border:
                    currentImageIndex === index
                      ? "2px solid #1976d2"
                      : "2px solid transparent",
                  opacity: currentImageIndex === index ? 1 : 0.7,
                  transition: "all 0.2s",
                  "&:hover": {
                    opacity: 1,
                    transform: "scale(1.05)",
                  },
                  flexShrink: 0,
                }}
              >
                <img
                  src={img || "/static/images/placeholder.png"}
                  alt={`Thumbnail ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/static/images/placeholder.png";
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
}

